/**
 * AdminSeederConsole — Studio → Seeder tab.
 *
 * Lists every one-off production seed job exposed by
 * routes/admin_seed.py, with a button to run it and a live JSON
 * result preview so admins can see whether the job inserted new
 * rows or was idempotently skipped.
 *
 * Adding a new job? Register it in the SEEDS array below.
 */
import { useState } from "react";
import { http } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Play, CheckCircle2, AlertTriangle, Database, TreePine, BookOpen, MapPin, CloudRain, Tag, Feather } from "lucide-react";

const SEEDS = [
  {
    key: "vellaringattu-tree",
    endpoint: "/admin/seed/vellaringattu-tree",
    label: "Vellaringattu Tree (324 members)",
    Icon: TreePine,
    detail: "Imports the GEDCOM at /app/imports/vellaringattu.bak into slug 'vellaringattu-family'. Soft-deletes any legacy 'vellaringattu-*-tree' stubs and sets redirect_to_slug so old bookmarks land correctly. Force-reimports if the current tree has < 50 members.",
  },
  {
    key: "vellaringattu-tree-purge",
    endpoint: "/admin/seed/vellaringattu-tree?purge=true",
    label: "Vellaringattu Tree — HARD PURGE the legacy stub",
    Icon: TreePine,
    detail: "Same as above, but physically DELETES the legacy 2-entry stub tree and its orphan members (no redirect written). Old bookmarks will 404. Use once, then use the regular seed above.",
  },
  {
    key: "vellaringattu-book",
    endpoint: "/admin/seed/vellaringattu-book",
    label: "Vellaringattu Family Book (258-page flipbook)",
    Icon: BookOpen,
    detail: "Rasterises /app/imports/vellaringattu_book.pdf into 258 JPEG pages, uploads them to object storage, and writes a family_documents row. Idempotent. May take 2-3 min on first run.",
  },
  {
    key: "pala-flood-2026",
    endpoint: "/admin/seed/pala-flood-2026",
    label: "Pala geo pages + Aug 1 2026 flood report",
    Icon: MapPin,
    detail: "Seeds Kerala/Kottayam/Pala + 13 villages as location documents and posts the Aug 1 2026 flood report.",
  },
  {
    key: "test-trees-public",
    endpoint: "/admin/seed/ensure-test-trees-public",
    label: "Featured test trees — make Vellaringattu + Kurisummoottil public on 4 sites",
    Icon: TreePine,
    detail: "Sets visibility=public and site_source=[socialpulse,kerala,indias,indianfamilytree] on both test trees so they surface on the Heritage page and the IndianFamilyTree sibling site.",
  },
  {
    key: "kerala-heritage-articles",
    endpoint: "/admin/seed/kerala-heritage-articles",
    label: "Kerala heritage article stubs (20 evergreen articles)",
    Icon: Feather,
    detail: "Writes 20 Kerala heritage article stubs (Nair, Syrian Christian, Namboothiri, Ezhava, Mappila, Cochin Jews, Pala, Onam, Vishu, Theyyam, Kathakali, Gulf migration, nursing diaspora, sadya, Aranmula Kannadi, etc.). Content-agent LLM back-fills full articles later.",
  },
  {
    key: "auto-tag",
    endpoint: "/curated-tags/auto-seed",
    label: "Auto-tag trees + memorials (curated_tags)",
    Icon: Tag,
    detail: "Rule-based auto-tagger — Vellaringattu/Kurisummoottil → notable+historic+kerala+diaspora; any tree with ≥100 members → notable; occupation-based memorial tags (military, healthcare, teachers, public-servants). Body: {\"dry_run\": false}.",
    body: { dry_run: false },
  },
  {
    key: "run-all",
    endpoint: "/admin/seed/run-all",
    label: "Run ALL seed jobs (recommended after a fresh deploy)",
    Icon: Database,
    detail: "Executes every seed job above in order. Each job is idempotent so this is always safe to click.",
    primary: true,
  },
  {
    key: "run-all-purge",
    endpoint: "/admin/seed/run-all?purge=true",
    label: "Run ALL seed jobs — with HARD PURGE of legacy Vellaringattu stub",
    Icon: Database,
    detail: "Same as Run ALL but physically deletes the legacy 2-entry Vellaringattu stub instead of soft-deleting it with a redirect. Use this if you want the old tree completely gone from the DB.",
  },
];

export default function AdminSeederConsole() {
  const [state, setState] = useState({});

  const run = async (job) => {
    setState((s) => ({ ...s, [job.key]: { status: "running" } }));
    try {
      const t0 = Date.now();
      const res = await http.post(job.endpoint, job.body || undefined);
      const ms = Date.now() - t0;
      setState((s) => ({ ...s, [job.key]: { status: "ok", ms, result: res.data } }));
      toast.success(`${job.label} — done in ${ms}ms`);
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || "Seed failed";
      setState((s) => ({ ...s, [job.key]: { status: "error", error: detail, raw: err?.response?.data } }));
      toast.error(`${job.label} — ${detail}`);
    }
  };

  return (
    <div data-testid="admin-seeder-console" className="space-y-4">
      <div>
        <div className="overline mb-2">ADMIN · SEEDER CONSOLE</div>
        <h2 className="font-display text-2xl">One-off production seed jobs</h2>
        <p className="text-sm mt-1" style={{ color: "var(--wofy-text-soft)" }}>
          Every job below is idempotent — safe to click repeatedly. Use these
          after a fresh production deploy to import the family GEDCOM,
          rasterise the family book, and seed the Pala area location pages.
        </p>
      </div>

      <div className="rounded-lg border p-3 text-xs flex items-start gap-2"
           style={{ background: "#FFF7ED", borderColor: "#FED7AA", color: "#9A3412" }}>
        <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
        <span>
          You're running against the <b>current environment</b> (preview or
          production, whichever this admin panel is served from). If you
          need to seed production, first make sure you're signed in on
          <code className="mx-1 px-1 rounded bg-white">socialpulse.com/admin</code>
          then click the buttons here.
        </span>
      </div>

      <div className="grid gap-3">
        {SEEDS.map((job) => {
          const s = state[job.key] || {};
          const Icon = job.Icon;
          return (
            <div key={job.key} className="rounded-xl border p-4 flex flex-col gap-3"
                 style={{ borderColor: "var(--wofy-border)" }}
                 data-testid={`seed-card-${job.key}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-2 min-w-0">
                  <Icon className="w-4 h-4 mt-1 shrink-0" style={{ color: "var(--wofy-primary)" }} />
                  <div className="min-w-0">
                    <div className="font-semibold text-sm">{job.label}</div>
                    <div className="text-[11px] font-mono opacity-60 break-all">POST {job.endpoint}</div>
                    <div className="text-xs mt-1" style={{ color: "var(--wofy-text-soft)" }}>{job.detail}</div>
                  </div>
                </div>
                <button
                  onClick={() => run(job)}
                  disabled={s.status === "running"}
                  className={job.primary ? "wofy-btn-primary text-xs shrink-0" : "wofy-btn-ghost text-xs shrink-0"}
                  data-testid={`seed-run-${job.key}`}
                >
                  {s.status === "running" ? (
                    <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Running…</>
                  ) : (
                    <><Play className="w-3.5 h-3.5" /> Run job</>
                  )}
                </button>
              </div>

              {s.status === "ok" && (
                <div className="rounded border p-2 text-[11px] font-mono"
                     style={{ background: "#F0FDF4", borderColor: "#BBF7D0", color: "#166534" }}
                     data-testid={`seed-result-${job.key}`}>
                  <div className="flex items-center gap-1.5 mb-1">
                    <CheckCircle2 className="w-3.5 h-3.5" />
                    <span className="font-semibold">Success · {s.ms}ms</span>
                  </div>
                  <pre className="whitespace-pre-wrap break-all">{JSON.stringify(s.result, null, 2)}</pre>
                </div>
              )}
              {s.status === "error" && (
                <div className="rounded border p-2 text-[11px] font-mono"
                     style={{ background: "#FEF2F2", borderColor: "#FECACA", color: "#991B1B" }}
                     data-testid={`seed-error-${job.key}`}>
                  <div className="flex items-center gap-1.5 mb-1">
                    <AlertTriangle className="w-3.5 h-3.5" />
                    <span className="font-semibold">{s.error}</span>
                  </div>
                  {s.raw && <pre className="whitespace-pre-wrap break-all opacity-80">{JSON.stringify(s.raw, null, 2)}</pre>}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <WeatherAgentCard />
    </div>
  );
}


/** WeatherAgentCard — batch content generator for the Weather Almanac.
 *  Powered by POST /api/admin/weather-agent/run. Lets admins request
 *  N events with a focus (current/historic/mixed), region, and event
 *  types. Returns per-pick outcome (generated/skipped/failed). */
function WeatherAgentCard() {
  const [count, setCount] = useState(10);
  const [focus, setFocus] = useState("mixed");
  const [region, setRegion] = useState("");
  const [status, setStatus] = useState({});

  const run = async () => {
    setStatus({ running: true });
    try {
      const t0 = Date.now();
      const res = await http.post("/admin/weather-agent/run", {
        count: Number(count) || 10,
        focus,
        region: region.trim() || null,
      });
      setStatus({ ok: true, ms: Date.now() - t0, result: res.data });
      toast.success(
        `Weather Agent: generated ${res.data?.generated?.length || 0}, skipped ${res.data?.skipped?.length || 0}, failed ${res.data?.failed?.length || 0}`
      );
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || "Agent failed";
      setStatus({ error: detail, raw: err?.response?.data });
      toast.error(detail);
    }
  };

  return (
    <div className="rounded-xl border p-4 flex flex-col gap-3"
         style={{ borderColor: "var(--wofy-border)", background: "#FFFBEB" }}
         data-testid="weather-agent-card">
      <div className="flex items-start gap-2">
        <CloudRain className="w-4 h-4 mt-1 shrink-0" style={{ color: "#B45309" }} />
        <div className="min-w-0 flex-1">
          <div className="font-semibold text-sm">Weather Almanac Content Agent</div>
          <div className="text-[11px] font-mono opacity-60 break-all">POST /admin/weather-agent/run</div>
          <div className="text-xs mt-1" style={{ color: "var(--wofy-text-soft)" }}>
            Batch-generate rich Almanac records for real weather events. The
            LLM picks verifiable events from authoritative sources, dedupes
            against existing records, and writes full articles with
            geographic tags, media links, and community discussion prompts.
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
        <label className="flex flex-col gap-1">
          <span className="text-[10px] font-mono uppercase opacity-60">Count (1–25)</span>
          <input type="number" min={1} max={25} value={count}
                 onChange={(e) => setCount(e.target.value)}
                 className="px-2 py-1.5 rounded border text-sm"
                 style={{ borderColor: "var(--wofy-border)" }}
                 data-testid="wa-count" />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-[10px] font-mono uppercase opacity-60">Focus</span>
          <select value={focus} onChange={(e) => setFocus(e.target.value)}
                  className="px-2 py-1.5 rounded border text-sm"
                  style={{ borderColor: "var(--wofy-border)" }}
                  data-testid="wa-focus">
            <option value="current">Current (past 72h)</option>
            <option value="historic">Historic milestones</option>
            <option value="mixed">Mixed</option>
          </select>
        </label>
        <label className="flex flex-col gap-1 col-span-2">
          <span className="text-[10px] font-mono uppercase opacity-60">Region (optional)</span>
          <input value={region} onChange={(e) => setRegion(e.target.value)}
                 placeholder="e.g. Kerala, Pacific coast, Sahel"
                 className="px-2 py-1.5 rounded border text-sm"
                 style={{ borderColor: "var(--wofy-border)" }}
                 data-testid="wa-region" />
        </label>
      </div>

      <div className="flex justify-end">
        <button
          onClick={run}
          disabled={status.running}
          className="wofy-btn-primary text-xs"
          data-testid="wa-run"
        >
          {status.running
            ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Running (may take 1–3 min)…</>
            : <><Play className="w-3.5 h-3.5" /> Generate batch</>}
        </button>
      </div>

      {status.ok && (
        <div className="rounded border p-2 text-[11px] font-mono max-h-96 overflow-y-auto"
             style={{ background: "#F0FDF4", borderColor: "#BBF7D0", color: "#166534" }}
             data-testid="wa-result">
          <div className="flex items-center gap-1.5 mb-1">
            <CheckCircle2 className="w-3.5 h-3.5" />
            <span className="font-semibold">Success · {status.ms}ms</span>
          </div>
          <pre className="whitespace-pre-wrap break-all">{JSON.stringify(status.result, null, 2)}</pre>
        </div>
      )}
      {status.error && (
        <div className="rounded border p-2 text-[11px] font-mono"
             style={{ background: "#FEF2F2", borderColor: "#FECACA", color: "#991B1B" }}
             data-testid="wa-error">
          <div className="flex items-center gap-1.5 mb-1">
            <AlertTriangle className="w-3.5 h-3.5" />
            <span className="font-semibold">{status.error}</span>
          </div>
          {status.raw && <pre className="whitespace-pre-wrap break-all opacity-80">{JSON.stringify(status.raw, null, 2)}</pre>}
        </div>
      )}
    </div>
  );
}
